import classNames from 'classnames';
import { ReactElement, ReactNode, useCallback } from 'react';
import { SORTING_DIRECTION } from '~/constants/main';
import ColumnTitle, { IColumnTitle } from '~/view/components/ColumnTitle/ColumnTitle';
import ScrollableBlock from '~/view/containers/ScrollableBlock/ScrollableBlock';
import './style.scss';

export type TGridColumnTitle<T> = Omit<IColumnTitle<T>, 'currentField' | 'currentDir' | 'setFilters'>;

interface IFilters<T> {
  sortField?: T;
  sortDir?: SORTING_DIRECTION;
}

interface IProps<T> {
  gridTemplateClass: string;
  columnTitles: TGridColumnTitle<T>[];
  filters?: IFilters<T>;
  setFilters?: (filters: Partial<IFilters<T>>) => void;
  className?: string;
  bodyClassName?: string;
  children?: ReactNode;
  scrollClassName?: string;
}

const GridTable = <T,>({ columnTitles, ...props }: IProps<T>): ReactElement => {
  const setFilters = useCallback(
    (sortField: IFilters<T>['sortField'] | undefined, sortDir: IFilters<T>['sortDir'] | undefined) => {
      if (typeof props.setFilters === 'function') props.setFilters({ sortField, sortDir });
    },
    [props.setFilters]
  );

  return (
    <ScrollableBlock
      showScrollbar
      scrollbarSize="normal"
      scrollClassName={props.scrollClassName}
      className={classNames('grid-table__scroll-block', props.className)}
    >
      <div className={classNames('grid-table__header', props.gridTemplateClass)}>
        {columnTitles.map(({ title, columnName, ...columnProps }, index) => (
          <ColumnTitle
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...columnProps}
            key={title + '-' + String(index)}
            title={title}
            columnName={columnName}
            currentField={columnName ? props.filters?.sortField : undefined}
            currentDir={columnName ? props.filters?.sortDir : undefined}
            setFilters={setFilters}
          />
        ))}
      </div>

      <div className={classNames('grid-table__body', props.bodyClassName)}>{props.children}</div>
    </ScrollableBlock>
  );
};

export default GridTable;
